import { useEffect, type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../lib/utils';
import { useMediaQuery } from '../hooks/use-media-query';

/**
 * The shared master-detail layout.
 *
 * Every record surface in the platform is some version of "a list of things,
 * and the thing you picked". On a wide screen those sit side by side. The
 * failure mode this component exists to remove is what happens when that same
 * two-column grid collapses on a phone: the columns stack, so tapping a row in
 * a 200-row list renders its detail *below all 200 rows*. The record appears to
 * do nothing.
 *
 * Below `splitAt` this renders one pane at a time. Selecting a record swaps the
 * list out for the detail — a focused view with a back control — which is what
 * a tap on a row is understood to mean. Deselecting returns to the list.
 *
 * Selection state stays with the caller (usually a route param), so back
 * navigation, deep links, and permissions are untouched; only which pane is
 * painted changes.
 */
export function MasterDetail({
  list,
  detail,
  /** Width at which both panes fit side by side. Defaults to Tailwind `xl`. */
  splitAt = 'xl',
  /** Left column track used once split. */
  listWidth = 'minmax(280px,360px)',
  /** Right column track used once split. */
  detailWidth = 'minmax(0,1fr)',
  /**
   * Stable identity of the open record, e.g. its id. Drives the focused-view
   * scroll reset. Without it the reset only fires on entering the focused view.
   */
  detailKey,
  /** Called when the mobile back control is pressed. Should clear the selection. */
  onBack,
  /** Label for the mobile back control, e.g. "All tasks". */
  backLabel = 'Back to list',
  /** Rendered under the list column, above the fold on mobile. */
  footer,
  /** When true the detail owns the full width even on desktop. */
  expanded = false,
  className,
}: {
  list: ReactNode;
  detail?: ReactNode;
  splitAt?: 'lg' | 'xl' | '2xl';
  listWidth?: string;
  detailWidth?: string;
  detailKey?: string;
  onBack?: () => void;
  backLabel?: string;
  footer?: ReactNode;
  expanded?: boolean;
  className?: string;
}) {
  const hasDetail = Boolean(detail);
  const isSplit = useMediaQuery(SPLIT_QUERIES[splitAt]);
  const focused = hasDetail && !isSplit;

  /*
   * A record opened from halfway down a long list would otherwise inherit that
   * scroll position and start mid-detail.
   *
   * This resets on *which record is open*, never on the detail node itself.
   * `detail` is a fresh JSX element on every render, so depending on it made
   * any unrelated re-render — a polled unread count, a settings response
   * landing a second after load — throw the reader back to the top of a page
   * they were part-way through. On a phone, where the focused view is the whole
   * page, that reads as the page refusing to scroll.
   */
  useEffect(() => {
    if (!focused) return;
    const scroller = document.querySelector('main');
    scroller?.scrollTo({ top: 0 });
  }, [focused, detailKey]);

  if (expanded && hasDetail) {
    return <div className={cn('min-w-0', className)}>{detail}</div>;
  }

  if (focused) {
    return (
      <div className={cn('flex min-w-0 flex-col gap-3', className)}>
        {onBack && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 w-fit gap-1.5 px-2 text-[13px] text-muted-foreground"
            onClick={onBack}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            {backLabel}
          </Button>
        )}
        {detail}
      </div>
    );
  }

  return (
    <div
      className={cn('grid gap-4', hasDetail && SPLIT_CLASSES[splitAt], className)}
      style={
        hasDetail
          ? ({
              ['--cc-list-width' as string]: listWidth,
              ['--cc-detail-width' as string]: detailWidth,
            } as never)
          : undefined
      }
    >
      <div className="min-w-0 space-y-3">
        {list}
        {footer}
      </div>
      {detail}
    </div>
  );
}

/**
 * Tailwind cannot generate a class from a runtime string, so the split tracks
 * are static classes that read the caller's width from a CSS variable.
 */
const SPLIT_CLASSES = {
  lg: 'lg:grid-cols-[var(--cc-list-width)_var(--cc-detail-width)]',
  xl: 'xl:grid-cols-[var(--cc-list-width)_var(--cc-detail-width)]',
  '2xl': '2xl:grid-cols-[var(--cc-list-width)_var(--cc-detail-width)]',
} as const;

const SPLIT_QUERIES = {
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)',
  '2xl': '(min-width: 1536px)',
} as const;
